import { useEffect, useRef, useState } from 'react';
import { SchoolApi } from '../../../api/client';
import { Spinner } from '../../../components/ui';

interface Props {
  examName: string;
  gradeName: string;
  /** Bubbled questions (answer-grid rows). */
  nBubbled: number;
  /** Written questions the teacher grades. */
  nTeacher: number;
  /** 1-based question numbers of the teacher-graded questions. */
  teacherNumbers: number[];
  /** Question number printed beside each bubble row (defaults to 1..nBubbled). */
  bubbledNumbers?: number[];
}

const DEBOUNCE_MS = 400;

/**
 * The answer sheet exactly as it will print: the server draws it with the same
 * renderer as the downloadable PDF, so the preview and the PDF cannot differ.
 * Redrawn (debounced) when the name, grade or questions change.
 */
export default function AnswerSheetPreview({ examName, gradeName, nBubbled, nTeacher, teacherNumbers, bubbledNumbers }: Props) {
  const bubbled = bubbledNumbers ?? Array.from({ length: nBubbled }, (_, i) => i + 1);
  const key = JSON.stringify([examName, gradeName, bubbled, teacherNumbers]);
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'empty'>('loading');
  // The last drawn sheet stays on screen (under a spinner) while the next one is drawn.
  const shown = useRef<string | null>(null);
  useEffect(() => () => { if (shown.current) URL.revokeObjectURL(shown.current); }, []);

  useEffect(() => {
    if (bubbled.length === 0) { setState('empty'); return undefined; }
    const ctrl = new AbortController();
    setState('loading');
    const timer = window.setTimeout(() => {
      SchoolApi.paperSheetPreview({ name: examName, grade: gradeName, bubbled, teacher: teacherNumbers }, ctrl.signal)
        .then((blob) => {
          if (shown.current) URL.revokeObjectURL(shown.current);
          shown.current = URL.createObjectURL(blob);
          setUrl(shown.current);
          setState('ready');
        })
        .catch(() => { if (!ctrl.signal.aborted) setState('error'); });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const summary = `Answer sheet preview: roll number grid, ${nBubbled} bubbled ${nBubbled === 1 ? 'question' : 'questions'} with options A to D${nTeacher > 0 ? `, and ${nTeacher} teacher-graded ${nTeacher === 1 ? 'question' : 'questions'}` : ''}`;
  return (
    <div className="relative mx-auto aspect-[210/297] w-full max-w-[520px] overflow-hidden rounded-[6px] bg-surface shadow-card">
      {url && state !== 'empty' && <img src={url} alt={summary} className={`block h-full w-full ${state === 'loading' ? 'opacity-50' : ''}`} />}
      {state === 'loading' && (
        <div className="absolute inset-0 grid place-items-center" role="status" aria-label="Drawing the answer sheet"><Spinner /></div>
      )}
      {state === 'error' && <p role="alert" className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-muted">The sheet preview could not be drawn. It is still printed when you download it.</p>}
      {state === 'empty' && <p className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-muted">Add a multiple-choice question to see the answer sheet.</p>}
    </div>
  );
}
